'use client'

import React from 'react'
import { AmbientField } from '@/components/member/AmbientField'
import { OrbytMark } from '@/components/brand/OrbytMark'
import { communityOnOrbyt } from '@/lib/tenant/brand'
import '@/styles/preview.css'
import '@/styles/member-brand.css'

export interface MemberAuthCommunity {
  name?: string | null
  shortName?: string | null
  logoUrl?: string | null
}

interface MemberAuthShellProps {
  community?: MemberAuthCommunity | null
  /** Small meta under the brand name — e.g. "Member sign-in" */
  eyebrow?: string
  /** Optional progress / secondary header content under the brand stack */
  afterBrand?: React.ReactNode
  children: React.ReactNode
  mood?: 'calm' | 'signal'
  onClickCapture?: (e: React.MouseEvent) => void
  className?: string
}

/**
 * Shared columnar auth/onboarding frame.
 * Orbyt mark + community copy left-aligned; primary CTAs full-width, centered labels.
 */
export function MemberAuthShell({
  community,
  eyebrow,
  afterBrand,
  children,
  mood = 'calm',
  onClickCapture,
  className = '',
}: MemberAuthShellProps) {
  const name = community?.name?.trim() || null
  const brandLabel = name ? communityOnOrbyt(name) : 'Orbyt'
  const showBrand = Boolean(name || eyebrow)

  return (
    <div
      className={`member-brand preview-lane relative min-h-screen bg-canvas text-fg ${className}`.trim()}
      data-theme="member"
      onClickCapture={onClickCapture}
    >
      <AmbientField mood={mood} />
      <div className="relative z-[1] mx-auto flex min-h-screen w-full max-w-md flex-col px-5 pb-[max(2rem,env(safe-area-inset-bottom))] pt-[max(2.5rem,env(safe-area-inset-top))] text-left">
        {showBrand ? (
          <header className="mb-10 flex w-full flex-col items-start gap-4">
            <OrbytMark size={44} />
            <div className="w-full min-w-0">
              <p className="font-display text-[clamp(1.35rem,5vw,1.55rem)] font-light leading-snug tracking-tight text-fg">
                {brandLabel}
              </p>
              {eyebrow ? <p className="member-meta mt-1.5">{eyebrow}</p> : null}
            </div>
            {afterBrand ? <div className="w-full pt-1">{afterBrand}</div> : null}
          </header>
        ) : (
          <header className="mb-10">
            <OrbytMark size={44} />
          </header>
        )}
        <main className="flex w-full flex-1 flex-col items-stretch text-left">
          {children}
        </main>
      </div>
    </div>
  )
}

/** Secondary text action — always left-aligned under primary CTAs. */
export function AuthTextLink({
  children,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`mt-4 self-start text-left text-[13px] tracking-tight text-fg-muted underline-offset-2 hover:underline disabled:opacity-60 ${className}`.trim()}
      {...props}
    >
      {children}
    </button>
  )
}
